import { useAuthContext } from '@/auth/useAuthContext';
import {
  ApiError,
  type DaycareBurnOrder,
  type LatePickupPolicyDto,
  deleteLatePickupPolicy,
  getDaycareBurnOrder,
  getLatePickupPolicy,
  putDaycareBurnOrder,
  putLatePickupPolicy,
} from '@/lib/api';
import { useCallback, useEffect, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// Sub-spec 04 Plan 5 M2 + M3 — Late-pickup policy + burn-order toggle.
// Two adjacent settings combined on one page since both target the daycare
// vertical and both write to the active `accounts` row.
// ---------------------------------------------------------------------------

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  CAD: '$',
  AUD: '$',
  NZD: '$',
};

function currencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] ?? currency;
}

function minorToMajor(minor: number): string {
  return (minor / 100).toFixed(2);
}

function majorToMinor(major: string): number | null {
  const trimmed = major.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// ---------------------------------------------------------------------------
// Late pickup form
// ---------------------------------------------------------------------------

function LatePickupForm({
  initial,
  currency,
  onSaved,
  onCleared,
}: {
  initial: LatePickupPolicyDto | null;
  currency: string;
  onSaved: () => void;
  onCleared: () => void;
}) {
  const [enabled, setEnabled] = useState<boolean>(initial?.enabled ?? false);
  const [cutoffHour, setCutoffHour] = useState<string>(initial ? String(initial.cutoffHour) : '18');
  const [intervalMinutes, setIntervalMinutes] = useState<string>(
    initial ? String(initial.intervalMinutes) : '15',
  );
  const [perIntervalMajor, setPerIntervalMajor] = useState<string>(
    initial ? minorToMajor(initial.perIntervalMinor) : '5.00',
  );
  const [graceMinutes, setGraceMinutes] = useState<string>(
    initial?.graceMinutes !== undefined ? String(initial.graceMinutes) : '5',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const preview = useMemo(() => {
    const cutoff = Number(cutoffHour);
    const interval = Number(intervalMinutes);
    const perMinor = majorToMinor(perIntervalMajor) ?? 0;
    const grace = Number(graceMinutes);
    if (
      !Number.isFinite(cutoff) ||
      !Number.isFinite(interval) ||
      interval <= 0 ||
      !Number.isFinite(grace) ||
      grace < 0
    ) {
      return null;
    }
    return `Late pickup fee: ${currencySymbol(currency)}${minorToMajor(perMinor)} per ${interval} minutes after ${pad2(cutoff)}:00, with ${grace}-minute grace period.`;
  }, [cutoffHour, intervalMinutes, perIntervalMajor, graceMinutes, currency]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    const cutoff = Number(cutoffHour);
    const interval = Number(intervalMinutes);
    const perMinor = majorToMinor(perIntervalMajor);
    const grace = graceMinutes.trim() === '' ? undefined : Number(graceMinutes);

    if (!Number.isInteger(cutoff) || cutoff < 0 || cutoff > 23) {
      setError('Cutoff hour must be an integer between 0 and 23.');
      return;
    }
    if (!Number.isFinite(interval) || interval <= 0) {
      setError('Interval minutes must be a positive number.');
      return;
    }
    if (perMinor === null) {
      setError('Per-interval fee must be a non-negative number.');
      return;
    }
    if (grace !== undefined && (!Number.isFinite(grace) || grace < 0)) {
      setError('Grace minutes must be a non-negative number.');
      return;
    }

    setSaving(true);
    try {
      const body: LatePickupPolicyDto = {
        enabled,
        cutoffHour: cutoff,
        intervalMinutes: interval,
        perIntervalMinor: perMinor,
        currency,
        ...(grace !== undefined ? { graceMinutes: grace } : {}),
      };
      await putLatePickupPolicy(body);
      setSuccess('Saved.');
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError && err.body?.issues
          ? (err.body.issues as string[]).join('; ')
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleClear() {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await deleteLatePickupPolicy();
      setEnabled(false);
      onCleared();
    } catch {
      setError('Could not clear the policy. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSave(e)} className="space-y-4">
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="text-sm text-emerald-700" role="status">
          {success}
        </p>
      )}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          disabled={saving}
          className="size-4"
          data-testid="late-pickup-enabled"
        />
        <span>Enable late-pickup fees</span>
      </label>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="cutoff-hour"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Cutoff hour (facility-local)
          </label>
          <input
            id="cutoff-hour"
            type="number"
            min={0}
            max={23}
            value={cutoffHour}
            onChange={(e) => setCutoffHour(e.target.value)}
            disabled={saving}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Hour (0-23) at which late fees start accruing.
          </p>
        </div>
        <div>
          <label
            htmlFor="interval-mins"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Interval (minutes)
          </label>
          <input
            id="interval-mins"
            type="number"
            min={1}
            value={intervalMinutes}
            onChange={(e) => setIntervalMinutes(e.target.value)}
            disabled={saving}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        <div>
          <label
            htmlFor="per-interval-fee"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Fee per interval ({currencySymbol(currency)})
          </label>
          <input
            id="per-interval-fee"
            type="text"
            inputMode="decimal"
            value={perIntervalMajor}
            onChange={(e) => setPerIntervalMajor(e.target.value)}
            disabled={saving}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        <div>
          <label
            htmlFor="grace-mins"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Grace (minutes, optional)
          </label>
          <input
            id="grace-mins"
            type="number"
            min={0}
            value={graceMinutes}
            onChange={(e) => setGraceMinutes(e.target.value)}
            disabled={saving}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
      </div>

      {preview && (
        <div
          className="rounded-md border border-border bg-secondary/40 p-3 text-sm text-foreground"
          data-testid="late-pickup-preview"
        >
          {preview}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-2">
        <button
          type="button"
          onClick={() => void handleClear()}
          disabled={saving || !initial}
          className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground press disabled:opacity-50"
          data-testid="late-pickup-clear"
        >
          Clear policy
        </button>
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground press"
          data-testid="late-pickup-save"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Burn order card
// ---------------------------------------------------------------------------

function BurnOrderCard({
  initial,
  onSaved,
}: {
  initial: DaycareBurnOrder;
  onSaved: (order: DaycareBurnOrder) => void;
}) {
  const [order, setOrder] = useState<DaycareBurnOrder>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setOrder(initial);
  }, [initial]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await putDaycareBurnOrder(order);
      setSuccess(true);
      onSaved(order);
    } catch {
      setError('Could not save burn order. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="text-sm text-emerald-700" role="status">
          Saved.
        </p>
      )}
      <p className="text-sm text-muted-foreground">
        When a daycare visit is created and the customer has both a credit pack and an active
        membership, which one burns first? Packs expire 12 months after purchase; memberships reset
        on each billing cycle. There's no wrong choice — pick what your tenants prefer.
      </p>
      <div className="space-y-2">
        {(
          [
            {
              value: 'pack_first',
              title: 'Pack credits first',
              detail:
                'Burn pack credits before membership visits. Pack credits expire — use them while they last.',
            },
            {
              value: 'membership_first',
              title: 'Membership visits first',
              detail:
                'Burn membership visits before pack credits. Saves the more-expensive pack credits for later.',
            },
          ] as const
        ).map((opt) => (
          <label
            key={opt.value}
            className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 hover:bg-secondary/40"
          >
            <input
              type="radio"
              name="burn-order"
              value={opt.value}
              checked={order === opt.value}
              onChange={() => setOrder(opt.value)}
              disabled={saving}
              className="mt-0.5 size-4"
              data-testid={`burn-order-${opt.value}`}
            />
            <div>
              <p className="text-sm font-medium">{opt.title}</p>
              <p className="text-xs text-muted-foreground">{opt.detail}</p>
            </div>
          </label>
        ))}
      </div>
      <button
        type="button"
        onClick={() => void handleSave()}
        disabled={saving || order === initial}
        className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
        data-testid="burn-order-save"
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DaycareLatePickup() {
  const { state } = useAuthContext();
  const currency = state.me?.activeAccount?.currency ?? 'GBP';
  const [policy, setPolicy] = useState<LatePickupPolicyDto | null>(null);
  const [burnOrder, setBurnOrder] = useState<DaycareBurnOrder>('pack_first');
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setPermissionDenied(false);
    try {
      const [p, b] = await Promise.all([getLatePickupPolicy(), getDaycareBurnOrder()]);
      setPolicy(p.policy);
      setBurnOrder(b.order);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setPermissionDenied(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (permissionDenied) {
    return (
      <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
        <p className="text-sm font-medium text-foreground">
          You don't have permission to manage daycare settings
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Ask an owner or manager to grant access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Daycare late pickup</h1>
      </header>

      {loading ? (
        <div className="space-y-3">
          <div className="h-8 w-48 animate-pulse rounded bg-secondary" />
          <div className="h-32 w-full animate-pulse rounded bg-secondary" />
        </div>
      ) : (
        <>
          <section className="mb-8 rounded-2xl bg-surface p-6 ring-1 ring-border elevate">
            <h2 className="mb-4 font-heading text-lg font-medium">Late-pickup policy</h2>
            <LatePickupForm
              initial={policy}
              currency={currency}
              onSaved={() => void refresh()}
              onCleared={() => {
                setPolicy(null);
              }}
            />
          </section>

          <section className="rounded-2xl bg-surface p-6 ring-1 ring-border elevate">
            <h2 className="mb-4 font-heading text-lg font-medium">Burn order</h2>
            <BurnOrderCard initial={burnOrder} onSaved={(order) => setBurnOrder(order)} />
          </section>
        </>
      )}
    </div>
  );
}
